import { type CurrencyCode, requireCurrencyCode } from './currency.ts'
import { formatScaled, parseScaled } from './decimal.ts'
import { RATIO_SCALE, type Ratio } from './ratio.ts'

export const MONEY_SCALE = 4

/** Mirrors the contract's `ApiMoney.amount` pattern; asserted equal to it by test. */
export const MONEY_AMOUNT_PATTERN = /^-?\d{1,15}(\.\d{1,4})?$/

declare const MoneyBrand: unique symbol

/** A signed amount in one currency, held as integer units at {@link MONEY_SCALE}. */
export interface Money {
  readonly units: bigint
  readonly currency: CurrencyCode
  readonly [MoneyBrand]: true
}

export interface ApiMoney {
  readonly amount: string
  readonly currency: string
}

export class CurrencyMismatchError extends Error {
  readonly code = 'domain.currency_mismatch'
  readonly left: CurrencyCode
  readonly right: CurrencyCode
  constructor(left: CurrencyCode, right: CurrencyCode) {
    super(`Cannot combine ${left} with ${right}`)
    this.name = 'CurrencyMismatchError'
    this.left = left
    this.right = right
  }
}

function make(units: bigint, currency: CurrencyCode): Money {
  return { units, currency } as Money
}

export function money(amount: string, currency: CurrencyCode): Money {
  return make(parseScaled(amount, MONEY_SCALE, MONEY_AMOUNT_PATTERN), currency)
}

export function zeroMoney(currency: CurrencyCode): Money {
  return make(0n, currency)
}

export function fromApiMoney(value: ApiMoney): Money {
  return money(value.amount, requireCurrencyCode(value.currency))
}

export function toApiMoney(value: Money): ApiMoney {
  return { amount: formatScaled(value.units, MONEY_SCALE), currency: value.currency }
}

function sameCurrency(a: Money, b: Money): CurrencyCode {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency)
  return a.currency
}

export function addMoney(a: Money, b: Money): Money {
  return make(a.units + b.units, sameCurrency(a, b))
}

export function subtractMoney(a: Money, b: Money): Money {
  return make(a.units - b.units, sameCurrency(a, b))
}

export function negateMoney(value: Money): Money {
  return make(-value.units, value.currency)
}

export function absMoney(value: Money): Money {
  return value.units < 0n ? negateMoney(value) : value
}

export function multiplyMoney(value: Money, factor: bigint): Money {
  return make(value.units * factor, value.currency)
}

export function sumMoney(values: readonly Money[], currency: CurrencyCode): Money {
  return values.reduce((total, value) => addMoney(total, value), zeroMoney(currency))
}

export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b)
  return a.units < b.units ? -1 : a.units > b.units ? 1 : 0
}

export function equalsMoney(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.units === b.units
}

export function isZeroMoney(value: Money): boolean {
  return value.units === 0n
}

export function signOfMoney(value: Money): -1 | 0 | 1 {
  return value.units < 0n ? -1 : value.units > 0n ? 1 : 0
}

/** Splits `total` into `parts` equal-as-possible shares that sum back to it exactly -
 * the remainder (in minor units) goes one-by-one to the first shares, never dropped or
 * left for a caller to reconcile by hand. */
export function splitMoneyEvenly(total: Money, parts: number): Money[] {
  if (parts <= 0) return []
  const share = total.units / BigInt(parts)
  const remainder = total.units % BigInt(parts)
  return Array.from({ length: parts }, (_, index) =>
    make(share + (BigInt(index) < remainder ? 1n : 0n), total.currency),
  )
}

function roundedDivBigint(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n
  const n = numerator < 0n ? -numerator : numerator
  const d = denominator < 0n ? -denominator : denominator
  const rounded = (n * 2n + d) / (2n * d)
  return negative ? -rounded : rounded
}

/**
 * Percentage change of `current` against `base`, as a {@link Ratio} already scaled to a
 * percentage (5.23 means 5.23%, matching `formatRatio` + '%'). A negative or zero `base`
 * makes the result backwards or undefined, not merely surprising - see AGENTS.md's money
 * rules - so this returns `undefined` rather than a technically-computed, actually-false
 * number for a currency that can legitimately go negative (net worth, a loan balance).
 */
export function moneyChangeRatio(current: Money, base: Money): Ratio | undefined {
  sameCurrency(current, base)
  return percentChangeUnits(current.units, base.units)
}

/** `value` as a share of `total` (same currency), or `undefined` when `total` is zero. */
export function moneyShare(value: Money, total: Money): Ratio | undefined {
  sameCurrency(value, total)
  if (total.units === 0n) return undefined
  return { units: roundedDivBigint(value.units * 10n ** BigInt(RATIO_SCALE), total.units) } as Ratio
}

/**
 * {@link moneyChangeRatio} for a whole count with no currency (kilometres driven): the same
 * percentage scaling, and the same `undefined` for a base that is not positive.
 */
export function countChangeRatio(current: number, base: number): Ratio | undefined {
  return percentChangeUnits(BigInt(current), BigInt(base))
}

function percentChangeUnits(current: bigint, base: bigint): Ratio | undefined {
  if (base <= 0n) return undefined
  return { units: roundedDivBigint((current - base) * 100n * 10n ** BigInt(RATIO_SCALE), base) } as Ratio
}

/**
 * Where `value` sits between `min` and `max`, as 0..1, for chart geometry only - never
 * for a displayed figure. bigint until the one unavoidable float division a screen
 * coordinate requires.
 */
export function moneyProportion(value: Money, min: Money, max: Money): number {
  sameCurrency(min, max)
  sameCurrency(value, min)
  const range = max.units - min.units
  if (range <= 0n) return 0.5
  return Number(value.units - min.units) / Number(range)
}

/**
 * The inverse of {@link moneyProportion}: the value at `fraction` (clamped to 0..1) between
 * `min` and `max` - for a chart's gridline labels, which are real figures and must be exact
 * Money, not the float `moneyProportion` returns.
 */
export function moneyAtProportion(min: Money, max: Money, fraction: number): Money {
  const currency = sameCurrency(min, max)
  const clamped = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction
  const range = max.units - min.units
  return make(min.units + BigInt(Math.round(Number(range) * clamped)), currency)
}

function floorDiv(a: bigint, b: bigint): bigint {
  const q = a / b
  return a % b !== 0n && a < 0n ? q - 1n : q
}

function ceilDiv(a: bigint, b: bigint): bigint {
  const q = a / b
  return a % b !== 0n && a > 0n ? q + 1n : q
}

/** The classic "nice number" step for an axis: 1, 2 or 5 times a power of ten. */
function niceStep(rawStep: number): number {
  if (rawStep <= 0) return 1
  const exponent = Math.floor(Math.log10(rawStep))
  const fraction = rawStep / 10 ** exponent
  const niceFraction = fraction < 1.5 ? 1 : fraction < 3 ? 2 : fraction < 7 ? 5 : 10
  return niceFraction * 10 ** exponent
}

/**
 * Evenly spaced gridline values a person would pick by hand, covering at least
 * [min, max] - never the exact range sliced into equal parts, which is the clearest way an
 * axis announces it was computed rather than designed. Because the step is always a round
 * number and zero is always a multiple of it, zero lands exactly on one of these ticks
 * whenever the covered range spans it.
 */
export function niceMoneyTicks(min: Money, max: Money, targetCount: number): readonly Money[] {
  const currency = sameCurrency(min, max)
  if (equalsMoney(min, max)) return [min]
  const scale = 10 ** MONEY_SCALE
  const rawStep = Number(max.units - min.units) / scale / Math.max(1, targetCount - 1)
  const stepUnits = BigInt(Math.round(niceStep(rawStep) * scale))
  if (stepUnits <= 0n) return [min, max]
  const niceMinUnits = floorDiv(min.units, stepUnits) * stepUnits
  const niceMaxUnits = ceilDiv(max.units, stepUnits) * stepUnits
  const ticks: Money[] = []
  for (let units = niceMinUnits; units <= niceMaxUnits; units += stepUnits) {
    ticks.push(make(units, currency))
  }
  return ticks
}

/**
 * A value counting from zero toward `target` at `progress` (clamped to 0..1) - the one
 * count-up animation this app allows (a hero figure arriving for the first time). Sharing
 * `target`'s sign and never exceeding its magnitude is what keeps the animation from ever
 * overshooting or crossing zero; the final frame is always `target` itself, assigned.
 */
export function moneyTowardProgress(target: Money, progress: number): Money {
  const clamped = progress < 0 ? 0 : progress > 1 ? 1 : progress
  return make(BigInt(Math.round(Number(target.units) * clamped)), target.currency)
}

/**
 * Where a value and its bounds sit on one shared track, as fractions 0..1 - for a range
 * meter drawn over a figure the user did not choose a scale for. Works on the raw scaled
 * `units` of any one fixed-scale type (a Money's or a Ratio's), so every argument must share
 * that scale. The track spans the value and whichever bounds exist, padded by a fifth of the
 * spread on each side so nothing sits on the very edge; a missing bound leaves its side of the
 * zone open to that edge. Returns `undefined` when there is no bound at all to draw against.
 */
export function rangeTrackFractions(
  value: bigint,
  min: bigint | undefined,
  max: bigint | undefined,
): { readonly value: number; readonly zoneStart: number; readonly zoneEnd: number } | undefined {
  if (min === undefined && max === undefined) return undefined
  const points = [value, min, max].filter((point): point is bigint => point !== undefined)
  const lo = points.reduce((a, b) => (b < a ? b : a))
  const hi = points.reduce((a, b) => (b > a ? b : a))
  const spread = hi - lo
  const magnitude = hi < 0n ? -hi : hi
  const pad = spread > 0n ? spread / 5n : magnitude > 0n ? magnitude / 2n : 1n
  const start = lo - pad
  const range = hi + pad - start
  const at = (units: bigint): number => Number(units - start) / Number(range)
  return {
    value: at(value),
    zoneStart: min === undefined ? 0 : at(min),
    zoneEnd: max === undefined ? 1 : at(max),
  }
}
